import React, { useState } from 'react';
import '../profile-data-tables.css';

const ProfileDataTables = ({ employeeData, showMobileFooter = true, onDataChange, token, isEditable = true }) => {
  const [activeTab, setActiveTab] = useState('family');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
  const [editingItem, setEditingItem] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [formData, setFormData] = useState({});

  const tabs = [
    {
      key: 'family',
      label: 'Family',
      dataKey: 'family_members',
      apiEndpoint: '/auth/me',
      columns: [
        { key: 'name', label: 'Name', required: true },
        { key: 'date_of_birth', label: 'Date of birth', type: 'date', required: true },
        { key: 'relationship', label: 'Relationship', required: true },
        { key: 'aadhaar_number', label: 'Aadhaar number' }
      ],
      emptyMessage: 'No family members added'
    },
    {
      key: 'education',
      label: 'Education',
      dataKey: 'educations',
      apiEndpoint: '/auth/me',
      columns: [
        { key: 'qualification', label: 'Qualification', required: true },
        { key: 'year_of_passing', label: 'Year of passing', type: 'number', required: true },
        { key: 'institute_university', label: 'Institute / university', required: true },
        { key: 'marks_percentage', label: 'Marks / percentage', type: 'number' }
      ],
      emptyMessage: 'No educational qualifications added'
    },
    {
      key: 'employment',
      label: 'Previous Employment',
      dataKey: 'previous_employments',
      apiEndpoint: '/auth/me',
      columns: [
        { key: 'organization', label: 'Organization', required: true },
        { key: 'from_date', label: 'From', type: 'date', required: true },
        { key: 'to_date', label: 'To', type: 'date', required: true },
        { key: 'nature_of_work', label: 'Nature of work' }
      ],
      emptyMessage: 'No previous employment details added'
    }
  ];

  const activeTabConfig = tabs.find(t => t.key === activeTab);
  const data = employeeData?.[activeTabConfig?.dataKey] || [];
  
  // Debug: Log education data to see actual field names
  if (activeTab === 'education' && data.length > 0) {
    console.log('Education data from backend:', JSON.stringify(data[0], null, 2));
    console.log('Available fields:', Object.keys(data[0]));
  }

  const safeValue = (value, row, column) => {
    // Try main key first
    let val = value;
    
    // If empty and column has alternative keys, try them
    if ((!val || val === '' || val === 'null' || val === 'undefined') && column?.altKeys && row) {
      for (const altKey of column.altKeys) {
        if (row[altKey] && row[altKey] !== 'null' && row[altKey] !== 'undefined') {
          val = row[altKey];
          break;
        }
      }
    }
    
    if (val === null || val === undefined || val === '' || String(val) === 'null' || String(val) === 'undefined') {
      return '';
    }
    return String(val);
  };

  const handleAdd = () => {
    const initialData = {};
    activeTabConfig.columns.forEach(col => {
      initialData[col.key] = '';
    });
    setFormData(initialData);
    setModalMode('add');
    setEditingItem(null);
    setEditingIndex(null);
    setIsModalOpen(true);
  };

  const handleEdit = (item, index) => {
    setFormData({ ...item });
    setModalMode('edit');
    setEditingItem(item);
    setEditingIndex(index);
    setIsModalOpen(true);
  };

  const handleDelete = async (item, index) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) {
      return;
    }

    try {
      // Create updated array by removing the item at the specified index
      const currentDataArray = [...(employeeData?.[activeTabConfig.dataKey] || [])];
      currentDataArray.splice(index, 1);

      // Build the complete profile payload with the updated array
      const payload = {
        ...employeeData,
        [activeTabConfig.dataKey]: currentDataArray
      };

      // Remove employee_id from payload as it's not needed for /auth/me
      delete payload.employee_id;

      const url = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/auth/me`;

      console.log('Delete attempt:', {
        url: url,
        activeTab: activeTab,
        dataKey: activeTabConfig.dataKey,
        removedIndex: index,
        updatedArray: currentDataArray
      });

      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        // Notify parent to refresh data
        if (onDataChange) {
          onDataChange();
        }
      } else {
        const error = await response.json().catch(() => ({}));
        console.log('Delete error response:', error);
        alert(error.message || 'Failed to delete entry');
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert('Error deleting entry');
    }
  };

  const handleSave = async () => {
    // Validate required fields
    const missingFields = activeTabConfig.columns
      .filter(col => col.required && !formData[col.key])
      .map(col => col.label);

    if (missingFields.length > 0) {
      alert(`Please fill in required fields: ${missingFields.join(', ')}`);
      return;
    }

    try {
      // Process form data to ensure correct data types
      const processedData = { ...formData };
      
      // Convert numeric fields to numbers for education tab
      if (activeTab === 'education') {
        if (processedData.year_of_passing) {
          processedData.year_of_passing = parseInt(processedData.year_of_passing) || 0;
        }
        if (processedData.marks_percentage) {
          processedData.marks_percentage = parseFloat(processedData.marks_percentage) || 0;
        }
      }
      
      // Ensure dates are in correct format (YYYY-MM-DD)
      activeTabConfig.columns.forEach(col => {
        if (col.type === 'date' && processedData[col.key]) {
          const dateValue = processedData[col.key];
          if (dateValue && !dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
            // Convert from DD/MM/YYYY or MM/DD/YYYY to YYYY-MM-DD
            const date = new Date(dateValue);
            if (!isNaN(date.getTime())) {
              processedData[col.key] = date.toISOString().split('T')[0];
            }
          }
        }
      });

      // Create updated arrays for the current tab
      const currentDataArray = [...(employeeData?.[activeTabConfig.dataKey] || [])];
      
      if (modalMode === 'edit' && editingIndex !== null) {
        // Update existing item
        currentDataArray[editingIndex] = processedData;
      } else {
        // Add new item
        currentDataArray.push(processedData);
      }

      // Build the complete profile payload
      const payload = {
        ...employeeData,
        [activeTabConfig.dataKey]: currentDataArray
      };

      // Remove employee_id from payload as it's not needed for /auth/me
      delete payload.employee_id;

      const url = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/auth/me`;
      
      // Debug logging
      console.log('Save attempt:', {
        mode: modalMode,
        url: url,
        activeTab: activeTab,
        dataKey: activeTabConfig.dataKey,
        updatedArray: currentDataArray,
        payload: payload
      });
      
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      console.log('Response status:', response.status);

      if (response.ok) {
        setIsModalOpen(false);
        // Notify parent to refresh data
        if (onDataChange) {
          onDataChange();
        }
      } else {
        const error = await response.json().catch(() => ({}));
        console.log('Error response:', error);
        alert(error.message || 'Failed to save entry');
      }
    } catch (error) {
      console.error('Save error:', error);
      alert('Error saving entry');
    }
  };

  const handleFormChange = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="profile-data-tables">
      <div className="profile-tabs-header">
        <div className="profile-tabs" role="tablist">
          {tabs.map(tab => (
            <button
              key={tab.key}
              role="tab"
              aria-selected={activeTab === tab.key}
              className={`profile-tab ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
              {/* Add button next to active tab in mobile */}
              {activeTab === tab.key && isEditable && (
                <span className="mobile-add-btn" onClick={(e) => { e.stopPropagation(); handleAdd(); }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                  </svg>
                </span>
              )}
            </button>
          ))}
        </div>
        
        {/* Desktop Add button */}
        {isEditable && (
          <button id="profile-add-button" className="profile-add-btn desktop-add-btn" onClick={handleAdd}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            Add
          </button>
        )}
      </div>

      <div className="profile-tab-content" role="tabpanel">
        {data.length > 0 ? (
          <div className="profile-data-table-wrap">
            <table className="profile-data-table">
              <thead>
                <tr>
                  {activeTabConfig.columns.map(col => (
                    <th key={col.key}>{col.label}</th>
                  ))}
                  {isEditable && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {data.map((row, index) => (
                  <tr key={index}>
                    {activeTabConfig.columns.map(col => (
                      <td key={col.key}>{safeValue(row[col.key], row, col)}</td>
                    ))}
                    {isEditable && (
                      <td className="profile-table-actions">
                        <button 
                          className="profile-action-btn edit" 
                          onClick={() => handleEdit(row, index)}
                          title="Edit"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                          </svg>
                        </button>
                        <button 
                          className="profile-action-btn delete" 
                          onClick={() => handleDelete(row, index)}
                          title="Delete"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6"></polyline>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                          </svg>
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="profile-data-empty">
            {activeTabConfig?.emptyMessage || 'No data available'}
          </div>
        )}
      </div>

      {showMobileFooter && (
        <div className="profile-mobile-footer">
          <p>Details are added by HR</p>
        </div>
      )}

      {isModalOpen && (
        <div className="profile-modal-backdrop" onClick={() => setIsModalOpen(false)}>
          <div className="profile-modal" onClick={(e) => e.stopPropagation()}>
            <div className="profile-modal-header">
              <h3>{modalMode === 'add' ? 'Add' : 'Edit'} {activeTabConfig.label}</h3>
              <button className="profile-modal-close" onClick={() => setIsModalOpen(false)}>×</button>
            </div>
            
            <div className="profile-modal-body">
              {activeTabConfig.columns.map(col => (
                <div key={col.key} className="profile-form-field">
                  <label>
                    {col.label} {col.required && <span className="required">*</span>}
                  </label>
                  <input
                    type={col.type || 'text'}
                    value={formData[col.key] || ''}
                    onChange={(e) => handleFormChange(col.key, e.target.value)}
                    required={col.required}
                  />
                </div>
              ))}
            </div>
            
            <div className="profile-modal-footer">
              <button className="profile-btn secondary" onClick={() => setIsModalOpen(false)}>
                Cancel
              </button>
              <button className="profile-btn primary" onClick={handleSave}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileDataTables;